import { uuidv4 } from '@finagraph/strongbox-nexus-client';

import {
    FieldGuideCompaniesResponse,
    FieldGuideConnectionResponse,
    FieldGuideConnectionStatusResponse,
    FieldGuideEngagementsResponse,
    FieldGuideErrorResponse,
    FieldGuidePbcRequestItem,
    FieldGuidePbcRequestsResponse,
} from '../Models/Api/strongbox.financialportal';

import { store } from '../index';
import { GetAuth0Token } from '../Store/User/Selectors';

const ConnectWithPatUrl = '/api/integrations/fieldguide/connection/pat';
const ConnectionStatusUrl = '/api/integrations/fieldguide/connect/status';

function buildBaseHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
        Accept: 'application/json',
    };

    const authToken = GetAuth0Token(store.getState());
    if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
    }

    return headers;
}

export interface FieldGuideConnectFailure {
    // The server's `reason` code (e.g. 'invalid_token'), or undefined when no reason could be read
    // (a bodyless 500, a network failure, or an unparsable body).
    reason: string | undefined;
    missingScopes?: string[];
}

export type FieldGuideConnectResult =
    | { succeeded: true; response: FieldGuideConnectionResponse }
    | { succeeded: false; failure: FieldGuideConnectFailure };

// An explicit type predicate, rather than narrowing on `result.succeeded`, because this project's
// strictNullChecks: false setting prevents TypeScript from narrowing this discriminated union via
// control flow analysis alone.
export function isFieldGuideConnectFailure(result: FieldGuideConnectResult): result is { succeeded: false; failure: FieldGuideConnectFailure } {
    return !result.succeeded;
}

/**
 * Submits a FieldGuide personal access token to connect the signed-in user's account.
 *
 * Unlike most services in this app, this never throws: every outcome (success, a rejected token,
 * FieldGuide/portal unavailability, or an unexpected failure) comes back as a FieldGuideConnectResult
 * so ConnectStep can render every case inline without a try/catch of its own. The token is sent in the
 * request body only; it is never logged or echoed back by this function.
 */
export async function ConnectWithPat(token: string): Promise<FieldGuideConnectResult> {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...buildBaseHeaders(),
        // The Website's middleware answers a PUT without a unique request id with 403 (replay protection).
        'X-Request-Id': uuidv4(),
    };

    let response: Response;
    try {
        response = await fetch(ConnectWithPatUrl, {
            method: 'PUT',
            credentials: 'same-origin',
            headers,
            body: JSON.stringify({ token }),
        });
    } catch {
        return { succeeded: false, failure: { reason: undefined } };
    }

    if (response.status === 200) {
        try {
            const body: FieldGuideConnectionResponse = await response.json();
            return { succeeded: true, response: body };
        } catch {
            return { succeeded: false, failure: { reason: undefined } };
        }
    }

    if (response.status === 422 || response.status === 503) {
        try {
            const body: FieldGuideErrorResponse = await response.json();
            return { succeeded: false, failure: { reason: body.reason, missingScopes: body.missingScopes } };
        } catch {
            return { succeeded: false, failure: { reason: undefined } };
        }
    }

    // A bodyless 500, or any other unexpected status.
    return { succeeded: false, failure: { reason: undefined } };
}

export type FieldGuideListCompaniesResult =
    | { succeeded: true; response: FieldGuideCompaniesResponse }
    // message is the server's plain-language message; absent when the caller should show its generic portal text.
    | { succeeded: false; message?: string };

export function isFieldGuideListCompaniesFailure(result: FieldGuideListCompaniesResult): result is { succeeded: false; message?: string } {
    return !result.succeeded;
}

const ListCompaniesUrl = '/api/integrations/fieldguide/companies';

function isCompanyItem(value: unknown): boolean {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const item = value as Record<string, unknown>;

    return typeof item.id === 'string' && typeof item.name === 'string';
}

function isCompaniesResponse(value: unknown): value is FieldGuideCompaniesResponse {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const body = value as Record<string, unknown>;

    return Array.isArray(body.items) && body.items.every(isCompanyItem) && isAbsentOrString(body.nextCursor);
}

function readBrowseErrorMessage(value: unknown): string | undefined {
    if (typeof value === 'object' && value !== null) {
        const message = (value as Record<string, unknown>).message;
        if (typeof message === 'string' && message) {
            return message;
        }
    }

    return undefined;
}

/**
 * Shared request handling for the FieldGuide browse-list endpoints (companies, engagements).
 *
 * Like ConnectWithPat, this never throws on a domain failure: a 422/502/503 carries the server's
 * plain-language message, and every other failure (network, bodyless 500, unparsable body, or a 200
 * whose body the boundary check rejects) has no message, so the caller shows its own portal text.
 *
 * @throws the underlying rejection when `signal` is aborted: cancellation is not a domain outcome.
 */
async function getBrowseList<T>(
    url: string,
    isValid: (body: unknown) => body is T,
    signal?: AbortSignal
): Promise<{ succeeded: true; response: T } | { succeeded: false; message?: string }> {
    const failed = (message?: string): { succeeded: false; message?: string } => ({ succeeded: false, message });
    const headers = buildBaseHeaders();

    let response: Response;
    try {
        response = await fetch(url, { method: 'GET', credentials: 'same-origin', headers, signal });
    } catch (error) {
        if (signal?.aborted) {
            throw error;
        }
        return failed();
    }

    try {
        if (response.status === 200) {
            const body: unknown = await response.json();
            return isValid(body) ? { succeeded: true, response: body } : failed();
        }

        if (response.status === 422 || response.status === 502 || response.status === 503) {
            const body: unknown = await response.json();
            return failed(readBrowseErrorMessage(body));
        }
    } catch (error) {
        if (signal?.aborted) {
            throw error;
        }
        return failed();
    }

    return failed();
}

/**
 * Lists the companies the signed-in user can choose from in their connected FieldGuide account.
 *
 * @throws the underlying rejection when `options.signal` is aborted: cancellation is not a domain outcome.
 */
export function ListFieldGuideCompanies(options?: { signal?: AbortSignal }): Promise<FieldGuideListCompaniesResult> {
    return getBrowseList<FieldGuideCompaniesResponse>(ListCompaniesUrl, isCompaniesResponse, options?.signal);
}

export type FieldGuideListEngagementsResult =
    | { succeeded: true; response: FieldGuideEngagementsResponse }
    // message is the server's plain-language message; absent when the caller should show its generic portal text.
    | { succeeded: false; message?: string };

export function isFieldGuideListEngagementsFailure(result: FieldGuideListEngagementsResult): result is { succeeded: false; message?: string } {
    return !result.succeeded;
}

function isEngagementItem(value: unknown): boolean {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const item = value as Record<string, unknown>;

    return typeof item.id === 'string' && typeof item.name === 'string' && typeof item.status === 'string'
        && isAbsentOrString(item.createdAt);
}

function isEngagementsResponse(value: unknown): value is FieldGuideEngagementsResponse {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const body = value as Record<string, unknown>;

    return Array.isArray(body.items) && body.items.every(isEngagementItem) && isAbsentOrString(body.nextCursor);
}

/**
 * Lists the open engagements of one FieldGuide company (client) the signed-in user can choose from.
 *
 * @throws the underlying rejection when `options.signal` is aborted: cancellation is not a domain outcome.
 */
export function ListFieldGuideEngagements(companyId: string, options?: { signal?: AbortSignal }): Promise<FieldGuideListEngagementsResult> {
    return getBrowseList<FieldGuideEngagementsResponse>(
        `/api/integrations/fieldguide/companies/${encodeURIComponent(companyId)}/engagements`,
        isEngagementsResponse,
        options?.signal
    );
}

export type FieldGuideListPbcRequestsResult =
    | { succeeded: true; requests: FieldGuidePbcRequestItem[] }
    // message is the server's plain-language message; absent when the caller should show its generic portal text.
    | { succeeded: false; message?: string };

export function isFieldGuideListPbcRequestsFailure(result: FieldGuideListPbcRequestsResult): result is { succeeded: false; message?: string } {
    return !result.succeeded;
}

function isPbcRequestItem(value: unknown): boolean {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const item = value as Record<string, unknown>;

    return typeof item.id === 'string'
        && typeof item.title === 'string'
        && isAbsentOrString(item.description)
        && isAbsentOrString(item.url)
        && isAbsentOrString(item.status);
}

function isPbcRequestsResponse(value: unknown): value is FieldGuidePbcRequestsResponse {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const body = value as Record<string, unknown>;

    return Array.isArray(body.items) && body.items.every(isPbcRequestItem) && isAbsentOrString(body.nextCursor);
}

/**
 * Lists the PBC requests of one FieldGuide engagement the signed-in user can map workbooks to.
 *
 * @throws the underlying rejection when `options.signal` is aborted: cancellation is not a domain outcome.
 */
export async function ListFieldGuidePbcRequests(engagementId: string, options?: { signal?: AbortSignal }): Promise<FieldGuideListPbcRequestsResult> {
    const result = await getBrowseList<FieldGuidePbcRequestsResponse>(
        `/api/integrations/fieldguide/engagements/${encodeURIComponent(engagementId)}/pbc-requests`,
        isPbcRequestsResponse,
        options?.signal
    );

    return result.succeeded
        ? { succeeded: true, requests: (result as { response: FieldGuidePbcRequestsResponse }).response.items }
        : { succeeded: false, message: (result as { message?: string }).message };
}

const KnownStatuses: readonly unknown[] = ['NotConnected', 'Connected', 'ReconnectRequired'];
const KnownCredentialTypes: readonly unknown[] = ['OAuth', 'Pat'];

function isAbsentOrString(value: unknown): boolean {
    return value === undefined || value === null || typeof value === 'string';
}

function isConnectionStatusResponse(value: unknown): value is FieldGuideConnectionStatusResponse {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const body = value as Record<string, unknown>;

    return KnownStatuses.includes(body.status)
        && isAbsentOrString(body.connectionId)
        && isAbsentOrString(body.reason)
        && isAbsentOrString(body.fieldGuideEmail)
        && isAbsentOrString(body.fieldGuideDisplayName)
        && (body.credentialType === undefined || body.credentialType === null || KnownCredentialTypes.includes(body.credentialType));
}

export type FieldGuideStatusResult =
    | { outcome: 'status'; response: FieldGuideConnectionStatusResponse }
    | { outcome: 'unavailable' }
    | { outcome: 'forbidden' };

/**
 * Reads the signed-in user's FieldGuide connection status. Never throws: a 403 comes back as
 * 'forbidden', and a 503, a network failure, a 500, any other unexpected status, an unparsable 200,
 * or a 200 whose body is not a well-typed status response all come back as 'unavailable'.
 *
 * @throws the underlying rejection when `options.signal` is aborted: cancellation is not a domain outcome.
 */
export async function GetConnectionStatus(options?: { signal?: AbortSignal }): Promise<FieldGuideStatusResult> {
    const headers = buildBaseHeaders();
    const signal = options?.signal;

    let response: Response;
    try {
        response = await fetch(ConnectionStatusUrl, {
            method: 'GET',
            credentials: 'same-origin',
            headers,
            signal,
        });
    } catch (error) {
        if (signal?.aborted) {
            throw error;
        }
        return { outcome: 'unavailable' };
    }

    if (response.status === 403) {
        return { outcome: 'forbidden' };
    }

    if (response.status !== 200) {
        return { outcome: 'unavailable' };
    }

    try {
        const body: unknown = await response.json();
        if (!isConnectionStatusResponse(body)) {
            return { outcome: 'unavailable' };
        }

        return { outcome: 'status', response: body };
    } catch (error) {
        if (signal?.aborted) {
            throw error;
        }
        return { outcome: 'unavailable' };
    }
}
